import { Link } from 'react-router';
import { LandingPagesNavbar, LandingPagesFooter } from '@podus/ui';
import { POSTS } from '@/blog/posts';
import { useSeo } from '@/blog/useSeo';

const SITE = 'https://podus.ai';

export default function Blog() {
    useSeo({
        title: 'Podus AI Blog — AI for Justice, Tech for Good & Nigeria’s AI Ecosystem',
        description:
            'Stories and analysis on AI for justice, access to justice in Nigeria, tech for good, and the emerging African AI ecosystem — from the team behind Podus AI.',
        canonical: `${SITE}/blog`,
        jsonLd: {
            '@context': 'https://schema.org',
            '@type': 'Blog',
            'name': 'Podus AI Blog',
            'url': `${SITE}/blog`,
            'publisher': { '@type': 'Organization', 'name': "Citizens' Gavel", 'url': 'https://podus.org' },
            'blogPost': POSTS.map((p) => ({
                '@type': 'BlogPosting',
                'headline': p.title,
                'url': `${SITE}/blog/${p.slug}`,
                'datePublished': p.date,
                'description': p.description,
            })),
        },
    });

    return (
        <div className="page-scroll-root bg-white">
            <LandingPagesNavbar product="podus-ai" />

            <main className="mx-auto max-w-[1100px] px-4 pb-24 pt-12 lg:px-16 lg:pt-16 xl:px-20">
                <header className="max-w-2xl">
                    <p className="text-[13px] font-semibold uppercase tracking-[0.18em] text-primary">Podus AI Blog</p>
                    <h1 className="mt-4 text-[clamp(2rem,5vw,3.25rem)] font-bold leading-[1.1] text-secondary">
                        AI for Justice, told from the ground.
                    </h1>
                    <p className="mt-5 text-[16px] leading-relaxed text-[#747474]">
                        Analysis on access to justice in Nigeria, tech for good, and the emerging African AI ecosystem —
                        from the team building Podus AI.
                    </p>
                </header>

                <div className="mt-12 grid gap-6 sm:grid-cols-2">
                    {POSTS.map((post) => (
                        <article
                            key={post.slug}
                            className="group flex flex-col rounded-2xl border border-[#ececec] p-6 transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-[0_10px_30px_rgba(0,0,0,0.06)]"
                        >
                            <div className="flex flex-wrap items-center gap-2 text-[12px] text-[#9a9a9a]">
                                <time dateTime={post.date}>
                                    {new Date(post.date).toLocaleDateString('en-NG', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                    })}
                                </time>
                                <span>·</span>
                                <span>{post.readingTime}</span>
                            </div>

                            <h2 className="mt-4 text-[20px] font-bold leading-snug text-secondary transition-colors group-hover:text-primary">
                                <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                            </h2>

                            <p className="mt-3 flex-1 text-[14px] leading-relaxed text-[#434343]">{post.excerpt}</p>

                            <div className="mt-5 flex flex-wrap gap-2">
                                {post.tags.slice(0, 3).map((tag) => (
                                    <span
                                        key={tag}
                                        className="rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-semibold text-primary"
                                    >
                                        {tag}
                                    </span>
                                ))}
                            </div>

                            <Link
                                to={`/blog/${post.slug}`}
                                className="mt-6 inline-flex items-center gap-1.5 text-[13px] font-semibold text-secondary transition-colors group-hover:text-primary"
                            >
                                Read article <span aria-hidden="true">&rarr;</span>
                            </Link>
                        </article>
                    ))}
                </div>
            </main>

            <LandingPagesFooter />
        </div>
    );
}
